import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SeoHead from '@/components/base/SeoHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl, articleContent, seoArticles } from '@/mocks/siteData';

const siteUrl = import.meta.env.VITE_SITE_URL;

const articleImages: Record<string, string> = {
  'what-is-oopbuyspreadsheet': 'https://readdy.ai/api/search-image?query=Organized%20desk%20workspace%20with%20open%20laptop%20showing%20colorful%20spreadsheet%20dashboard%20next%20to%20a%20steaming%20ceramic%20coffee%20mug%20and%20neatly%20stacked%20fashion%20magazines%2C%20soft%20morning%20sunlight%20streaming%20through%20sheer%20linen%20curtains%2C%20warm%20beige%20and%20oatmeal%20tones%2C%20minimalist%20Scandinavian%20interior%20aesthetic&width=1200&height=600&seq=art-hero-pillar-v2a&orientation=landscape',
  'oopbuyspreadsheet-vs-excel': 'https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20concept%20with%20modern%20tablet%20showing%20sleek%20dashboard%20interface%20on%20left%20side%20and%20traditional%20printed%20spreadsheet%20paperwork%20on%20right%20side%2C%20neutral%20studio%20background%2C%20soft%20diffused%20lighting%2C%20warm%20ivory%20and%20charcoal%20color%20palette&width=1200&height=600&seq=art-hero-excel-v2b&orientation=landscape',
  'oopbuyspreadsheet-vs-notion': 'https://readdy.ai/api/search-image?query=Two%20laptops%20side%20by%20side%20on%20wooden%20desk%20showing%20different%20software%20interfaces%20with%20sticky%20notes%20around%20them%2C%20bright%20airy%20home%20office%20with%20natural%20daylight%2C%20warm%20terracotta%20and%20cream%20accents%2C%20organized%20creative%20workspace%20aesthetic&width=1200&height=600&seq=art-hero-notion-v2c&orientation=landscape',
  'oopbuyspreadsheet-beginner-guide': 'https://readdy.ai/api/search-image?query=Welcoming%20beginner%20friendly%20desk%20setup%20with%20open%20notebook%20handwritten%20checklist%20next%20to%20tablet%2C%20small%20potted%20succulent%20plant%20and%20colorful%20pens%20arranged%20neatly%2C%20soft%20golden%20hour%20light%20from%20window%2C%20warm%20inviting%20cream%20and%20sage%20green%20tones&width=1200&height=600&seq=art-hero-beginner-v2d&orientation=landscape',
  'oopbuyspreadsheet-fashion-buyers': 'https://readdy.ai/api/search-image?query=Fashion%20moodboard%20desk%20scene%20with%20fabric%20swatches%20color%20palette%20cards%20and%20designer%20sketches%20spread%20around%20laptop%2C%20elegant%20scissors%20and%20measuring%20tape%20accessories%2C%20bright%20studio%20lighting%2C%20sophisticated%20cream%20and%20dusty%20rose%20color%20scheme&width=1200&height=600&seq=art-hero-fashion-v2e&orientation=landscape',
  'oopbuyspreadsheet-sneaker-business': 'https://readdy.ai/api/search-image?query=Sneaker%20collector%20desk%20setup%20with%20limited%20edition%20shoe%20boxes%20stacked%20artistically%20next%20to%20laptop%20showing%20sales%20tracker%2C%20sneaker%20cleaning%20kit%20visible%2C%20warm%20amber%20accent%20lighting%2C%20urban%20contemporary%20aesthetic%2C%20cream%20concrete%20textured%20background&width=1200&height=600&seq=art-hero-sneaker-v2f&orientation=landscape',
  'oopbuyspreadsheet-cost-tracking': 'https://readdy.ai/api/search-image?query=Financial%20planning%20desk%20with%20calculator%20receipts%20coins%20and%20laptop%20showing%20expense%20tracking%20charts%2C%20neat%20organized%20paperwork%20stacks%20with%20highlighters%2C%20soft%20overhead%20lighting%2C%20warm%20ivory%20and%20muted%20gold%20tones%2C%20professional%20accounting%20aesthetic&width=1200&height=600&seq=art-hero-cost-v2g&orientation=landscape',
  'oopbuyspreadsheet-automation': 'https://readdy.ai/api/search-image?query=Futuristic%20minimalist%20desk%20with%20smart%20devices%20connected%20wirelessly%20to%20laptop%20showing%20automated%20workflow%20diagrams%2C%20geometric%20desk%20accessories%20in%20matte%20black%20and%20warm%20beige%2C%20soft%20ambient%20lighting%20with%20gentle%20glow%20effects%2C%20sleek%20modern%20tech%20aesthetic&width=1200&height=600&seq=art-hero-auto-v2h&orientation=landscape',
  'oopbuyspreadsheet-templates': 'https://readdy.ai/api/search-image?query=Creative%20flatlay%20of%20printed%20template%20sheets%20with%20elegant%20typography%20arranged%20around%20tablet%20device%2C%20washi%20tape%20and%20paper%20clips%20adding%20artistic%20touches%2C%20soft%20natural%20window%20light%2C%20warm%20cream%20and%20muted%20terracotta%20color%20palette%2C%20crafty%20organized%20aesthetic&width=1200&height=600&seq=art-hero-templates-v2i&orientation=landscape',
  'oopbuyspreadsheet-bulk-orders': 'https://readdy.ai/api/search-image?query=Warehouse%20style%20inventory%20desk%20with%20shipping%20labels%20packing%20tape%20and%20cardboard%20boxes%20stacked%20neatly%2C%20industrial%20laptop%20showing%20logistics%20dashboard%2C%20warm%20tungsten%20lighting%2C%20rustic%20wood%20tabletop%20surface%2C%20cream%20and%20warm%20brown%20tones%2C%20practical%20organized%20aesthetic&width=1200&height=600&seq=art-hero-bulk-v2j&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = seoArticles.find((a) => a.slug === slug);
  const content = article ? (articleContent[slug || ''] || null) : null;
  const heroImage = article ? (articleImages[slug || ''] || articleImages['what-is-oopbuyspreadsheet']) : '';
  const canonicalUrl = article ? `${siteUrl}/article/${article.slug}` : siteUrl;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article || !content) {
    return (
      <main className="min-h-screen bg-background-50">
        <SeoHead
          title="Article Not Found | Oopbuyspreadsheet"
          description="The requested article could not be found. Browse our collection of oopbuy spreadsheet guides, comparisons, and resources."
          canonicalUrl={siteUrl}
        />
        <Navbar />
        <div className="pt-24 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors">Back to Home</Link>
        </div>
        <Footer />
      </main>
    );
  }

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      image: heroImage,
      author: {
        '@type': 'Organization',
        name: 'Oopbuyspreadsheet',
        url: siteUrl,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Oopbuyspreadsheet',
        url: siteUrl,
      },
      datePublished: '2026-06-22',
      dateModified: '2026-06-22',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.title,
          item: canonicalUrl,
        },
      ],
    },
  ];

  const seoTitle = `${article.title} | Oopbuyspreadsheet`;

  return (
    <main className="min-h-screen bg-background-50">
      <SeoHead
        title={seoTitle}
        description={article.excerpt}
        keywords={`oopbuyspreadsheet, oopbuy spreadsheet, ${article.title.toLowerCase()}, spreadsheet tracking, purchase management`}
        canonicalUrl={canonicalUrl}
        ogTitle={seoTitle}
        ogDescription={article.excerpt}
        ogUrl={canonicalUrl}
        ogImage={heroImage}
        ogType="article"
        twitterCard="summary_large_image"
        twitterTitle={seoTitle}
        twitterDescription={article.excerpt}
        jsonLd={articleJsonLd}
      />
      <Navbar />

      <article className="pt-24 md:pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
              <li>
                <Link to="/" className="hover:text-foreground-700 transition-colors whitespace-nowrap">Home</Link>
              </li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-600">{article.title.length > 40 ? article.title.slice(0, 40) + '...' : article.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm text-foreground-400">
              Last updated: June 22, 2026 &bull; 8 min read
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-auto object-cover"
            />
          </div>

          <div className="prose-custom text-base leading-relaxed text-foreground-700 space-y-6">
            <section>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">Introduction</h2>
              <p>{content.intro}</p>
            </section>

            {content.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                <p>{section.body}</p>
              </section>
            ))}

            <section className="pt-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Conclusion
              </h2>
              <p>
                The modern buying landscape demands tools that work as hard as you do. <strong>Oopbuyspreadsheet</strong> delivers the automation, accuracy, and scalability that serious buyers need to stay competitive. Whether you are just starting your first spreadsheet system or looking to upgrade from manual tracking, the <strong>oopbuy spreadsheet</strong> methodology provides everything you need to take control of your purchasing operation.
              </p>
              <p className="font-semibold text-foreground-900 mt-4">
                Ready to transform your buying workflow? Start using oopbuyspreadsheet today and experience the difference that organized, automated tracking makes in your business.
              </p>
            </section>

            <div className="mt-10 p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-200/50">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">
                Get Your Free Oopbuyspreadsheet Template
              </h3>
              <p className="text-sm text-foreground-600 mb-4">
                Start tracking your purchases like a pro. Access the full oopbuy spreadsheet system with our free starter template — no sign-up required.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Download Free Template
                  <i className="ri-download-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-foreground-200 text-foreground-700 font-medium text-sm hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back to Home
                  <i className="ri-home-line"></i>
                </Link>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-background-200/70">
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">
                Related Resources on Oopbuyspreadsheets
              </h4>
              <div className="flex flex-wrap gap-2">
                <Link to="/" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  Oopbuyspreadsheet Home
                </Link>
                {seoArticles.filter(a => a.slug !== slug).slice(0, 6).map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {a.title.length > 35 ? a.title.slice(0, 35) + '...' : a.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}